// Password encryption helper using native Web Crypto API
// Compatible with existing backend AES-256-GCM implementation

/**
 * Converts hex string to Uint8Array for use with Web Crypto API
 */
function hexToUint8Array(hexString: string): Uint8Array {
    const bytes = new Uint8Array(hexString.length / 2);
    for (let i = 0; i < hexString.length; i += 2) {
        bytes[i / 2] = parseInt(hexString.substr(i, 2), 16);
    }
    return bytes;
}

async function importKey(): Promise<CryptoKey> {
    // IMPORTANT: This key must match process.env.ENCRYPTION_KEY from your backend
    // Backend uses: const key = Buffer.from(process.env.ENCRYPTION_KEY, "hex");
    // Make sure this keyHex value equals your backend's ENCRYPTION_KEY environment variable
    const keyHex = "9f8c7a1e2b6d4f3a9c8b7a6e5d4c3b2a9f8c7a1e2b6d4f3a9c8b7a6e5d4c3b2a";
    const keyData = hexToUint8Array(keyHex);

    return await crypto.subtle.importKey(
        "raw",
        keyData.buffer as ArrayBuffer,
        { name: "AES-GCM" },
        false,
        ["encrypt", "decrypt"]
    );
}

/**
 * Encrypts a password using AES-GCM encryption
 * Returns format compatible with backend: "iv:encryptedData:authTag" (all in hex)
 * Matches backend format from encrypt() function
 */
export async function encryptPassword(password: string): Promise<string> {
    try {
        const key = await importKey();
        const iv = crypto.getRandomValues(new Uint8Array(12)); // 12 bytes IV (same as crypto.randomBytes(12))
        const encodedPassword = new TextEncoder().encode(password);

        const encryptedData = await crypto.subtle.encrypt(
            {
                name: "AES-GCM",
                iv: iv
            },
            key,
            encodedPassword
        );

        // In AES-GCM, the encrypted data includes the auth tag at the end
        const encryptedArray = new Uint8Array(encryptedData);
        const ciphertext = encryptedArray.slice(0, -16); // All but last 16 bytes
        const authTag = encryptedArray.slice(-16); // Last 16 bytes is the auth tag

        // Convert to hex format: "iv:encryptedData:authTag"
        const ivHex = Array.from(iv).map(b => b.toString(16).padStart(2, '0')).join('');
        const encryptedHex = Array.from(ciphertext).map(b => b.toString(16).padStart(2, '0')).join('');
        const authTagHex = Array.from(authTag).map(b => b.toString(16).padStart(2, '0')).join('');

        return `${ivHex}:${encryptedHex}:${authTagHex}`;
    } catch (error) {
        console.error('Encryption failed:', error);
        throw new Error('Failed to encrypt password');
    }
}

/**
 * Decrypts a password (for reference - matches your backend decrypt function)
 * Your backend should parse the "iv:encryptedData:authTag" format and decrypt
 * This function is included for reference but won't be used on the frontend
 */
export async function decryptPassword(encryptedPassword: string): Promise<string> {
    try {
        const key = await importKey();

        // Parse the "iv:encryptedData:authTag" format
        const [ivHex, encryptedHex, authTagHex] = encryptedPassword.split(':');

        const iv = hexToUint8Array(ivHex);
        const ciphertext = hexToUint8Array(encryptedHex);
        const authTag = hexToUint8Array(authTagHex);

        // Combine ciphertext and authTag for Web Crypto API
        const combined = new Uint8Array(ciphertext.length + authTag.length);
        combined.set(ciphertext);
        combined.set(authTag, ciphertext.length);

        const decryptedData = await crypto.subtle.decrypt(
            {
                name: "AES-GCM",
                iv: new Uint8Array(iv)
            },
            key,
            combined
        );

        return new TextDecoder().decode(decryptedData);
    } catch (error) {
        console.error('Decryption failed:', error);
        throw new Error('Failed to decrypt password');
    }
}